import React, { useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { DSButton, DSRating, DSSpacer, DSSurface, DSText } from '@zoe/ds-web'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { trackQuizCompletedWithCallback } from 'components/gtmV2'
import { useLocation } from 'react-router-dom'
import { getQuizRouteConfig } from '../versioned-components/v5/quizRouting'
import DEFAULT_QUIZ_CONFIG from '../../config/quiz.config'
import { appendLeadId } from 'libs/attribution'

type Testimonial = {
  name: string
  quote: string
}

export type ResultsLoadingScreenProps = {
  title: string
  steps: string[]
  trustedByText: string
  testimonials: Testimonial[]
  buttonText: string
  exitPath: string
}

type Props = ResultsLoadingScreenProps

const StepRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const fillBar = keyframes`
  0% { width: 0%; }
  15% { width: 25%; }
  35% { width: 35%; }
  55% { width: 60%; }
  70% { width: 68%; }
  85% { width: 90%; }
  100% { width: 100%; }
`

const BarOuter = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 9999px;
  background: var(--colour-surface-cream-subtle, #faf8f5);
`

const BarInner = styled.div<{ $delay: number; $duration: number; $done: boolean }>`
  height: 8px;
  border-radius: 9999px;
  background: var(--colour-surface-yellow-bold, #ffd100);
  width: ${({ $done }) => ($done ? '100%' : '0%')};
  ${({ $done, $duration, $delay }) =>
    !$done &&
    css`
      animation: ${fillBar} ${$duration}ms linear ${$delay}ms forwards;
    `}
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const SpinnerDiv = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid var(--colour-surface-cream-bold, #ede8de);
  border-top-color: var(--colour-surface-yellow-bold, #ffd100);
  border-radius: 50%;
  animation: ${spin} 1.2s linear infinite;
  flex-shrink: 0;
`

const Spinner = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((props, ref) => (
  <SpinnerDiv ref={ref} {...props} />
))

const PercentageRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--grid-8);
`

const CARD_WIDTH = 320
const CARD_GAP = 12
const SLIDE_INTERVAL_MS = 3500

const CarouselTrack = styled.div<{ $offset: number; $animate: boolean }>`
  display: flex;
  gap: ${CARD_GAP}px;
  ${({ $animate }) => $animate && 'transition: transform 0.6s ease-in-out;'}
  transform: translateX(${({ $offset }) => $offset}px);
`

const CarouselWrapper = styled.div`
  overflow: hidden;
  margin: 0 calc(-1 * var(--grid-24));
  touch-action: pan-x;
  cursor: grab;
  mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);

  &:active {
    cursor: grabbing;
  }
`

// Different durations: 1st slowish, 2nd moderate, 3rd slower, 4th slowest
const STEP_DURATIONS = [2200, 1600, 2800, 3400]
const STEP_GAP_MS = 200

const KEYFRAME_STOPS = [
  [0, 0],
  [0.15, 25],
  [0.35, 35],
  [0.55, 60],
  [0.7, 68],
  [0.85, 90],
  [1, 100],
] as const

const interpolateProgress = (t: number): number => {
  if (t <= 0) return 0
  if (t >= 1) return 100
  for (let i = 1; i < KEYFRAME_STOPS.length; i++) {
    const [prevT, prevV] = KEYFRAME_STOPS[i - 1]
    const [currT, currV] = KEYFRAME_STOPS[i]
    if (t <= currT) {
      const segmentT = (t - prevT) / (currT - prevT)
      return Math.round(prevV + segmentT * (currV - prevV))
    }
  }
  return 100
}

const getStepStartTime = (index: number): number => {
  let t = 0
  for (let i = 0; i < index; i++) {
    t += STEP_DURATIONS[i] + STEP_GAP_MS
  }
  return t
}

const getTotalDuration = (stepCount: number): number => {
  return getStepStartTime(stepCount - 1) + STEP_DURATIONS[stepCount - 1]
}

// ── Isolated carousel component ──────────────────────────────────────
const TestimonialCarousel: React.FC<{ testimonials: Testimonial[] }> = React.memo(({ testimonials }) => {
  const [slideIndex, setSlideIndex] = useState(0)
  const [animateSlide, setAnimateSlide] = useState(true)
  const slideTimerRef = useRef<number>()
  const carouselRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const totalCards = testimonials.length
  const dragStartRef = useRef<number | null>(null)

  useEffect(() => {
    if (carouselRef.current) {
      setContainerWidth(carouselRef.current.offsetWidth)
    }
  }, [])

  useEffect(() => {
    slideTimerRef.current = window.setInterval(() => {
      setSlideIndex((prev) => {
        const next = prev + 1
        if (next >= totalCards) {
          setTimeout(() => {
            setAnimateSlide(false)
            setSlideIndex(0)
            setTimeout(() => setAnimateSlide(true), 50)
          }, 600)
        }
        return next
      })
    }, SLIDE_INTERVAL_MS)
    return () => clearInterval(slideTimerRef.current)
  }, [totalCards])

  const centerOffset = containerWidth ? containerWidth / 2 - CARD_WIDTH / 2 : 0
  const leftPadding = 24
  const cardOffset = slideIndex === 0 ? leftPadding : -(slideIndex * (CARD_WIDTH + CARD_GAP)) + centerOffset

  const handlePointerDown = (e: React.PointerEvent) => {
    dragStartRef.current = e.clientX
    if (slideTimerRef.current) clearInterval(slideTimerRef.current)
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    if (dragStartRef.current === null) return
    const delta = e.clientX - dragStartRef.current
    dragStartRef.current = null
    const threshold = 50
    if (delta < -threshold) {
      setSlideIndex((prev) => Math.min(prev + 1, totalCards - 1))
    } else if (delta > threshold) {
      setSlideIndex((prev) => Math.max(prev - 1, 0))
    }
  }

  return (
    <CarouselWrapper ref={carouselRef} onPointerDown={handlePointerDown} onPointerUp={handlePointerUp}>
      <CarouselTrack $offset={cardOffset} $animate={animateSlide}>
        {[...testimonials, ...testimonials].map((t, i) => (
          <DSSurface key={i} surfaceColor="cream-subtle" radius={12} padding={24}>
            <div style={{ width: CARD_WIDTH - 48, textAlign: 'center' }}>
              <DSRating value={5} size={24} color="brand" />
              <DSSpacer size={12} direction="vertical" />
              <DSText variant="fluid-paragraph-200">&ldquo;{t.quote}&rdquo;</DSText>
              <DSSpacer size={8} direction="vertical" />
              <DSText variant="fluid-paragraph-200" weight="semiBold">
                {t.name}
              </DSText>
            </div>
          </DSSurface>
        ))}
      </CarouselTrack>
    </CarouselWrapper>
  )
})

TestimonialCarousel.displayName = 'TestimonialCarousel'

// Module-level state that survives React remounts
let globalAnimationStart = 0
let globalAnimationDone = false

// ── Main screen component ────────────────────────────────────────────
export const ResultsLoadingScreen: React.FC<Props> = React.memo(
  ({ title, steps, trustedByText, testimonials, buttonText, exitPath: defaultExitPath }) => {
    const location = useLocation()
    const { quizVersion } = getQuizRouteConfig(location.pathname)
    const { productType, exitPath = defaultExitPath } = DEFAULT_QUIZ_CONFIG[quizVersion] ?? {}
    const [isComplete, setIsComplete] = useState(globalAnimationDone)
    const rafRef = useRef<number>(0)
    const percentageRefs = useRef<(HTMLSpanElement | null)[]>([])
    const spinnerRefs = useRef<(HTMLDivElement | null)[]>([])

    useEffect(() => {
      if (globalAnimationDone) {
        // Animation already finished in a previous mount — show final state
        steps.forEach((_, i) => {
          const el = percentageRefs.current[i]
          if (el) el.textContent = '100%'
        })
        return
      }

      const startTimer = setTimeout(
        () => {
          if (!globalAnimationStart) globalAnimationStart = performance.now()

          const animate = (timestamp: number) => {
            const elapsed = timestamp - globalAnimationStart

            steps.forEach((_, i) => {
              const stepStart = getStepStartTime(i)
              const stepDuration = STEP_DURATIONS[i]
              let pct = 0
              if (elapsed >= stepStart) {
                const t = Math.min((elapsed - stepStart) / stepDuration, 1)
                pct = interpolateProgress(t)
              }

              const el = percentageRefs.current[i]
              if (el) el.textContent = pct > 0 ? `${pct}%` : ''

              const spinnerEl = spinnerRefs.current[i]
              if (spinnerEl) spinnerEl.style.display = pct > 0 && pct < 100 ? 'block' : 'none'
            })

            const totalDuration = getTotalDuration(steps.length)
            if (elapsed < totalDuration) {
              rafRef.current = requestAnimationFrame(animate)
            } else {
              globalAnimationDone = true
              steps.forEach((_, i) => {
                const el = percentageRefs.current[i]
                if (el) el.textContent = '100%'
                const spinnerEl = spinnerRefs.current[i]
                if (spinnerEl) spinnerEl.style.display = 'none'
              })
              setTimeout(() => setIsComplete(true), 400)
            }
          }

          rafRef.current = requestAnimationFrame(animate)
        },
        globalAnimationStart ? 0 : 100,
      )

      return () => {
        clearTimeout(startTimer)
        cancelAnimationFrame(rafRef.current)
      }
    }, [steps])

    const handleDiscover = () => {
      trackQuizCompletedWithCallback(quizVersion, productType, () => {
        window.location.href = appendLeadId(exitPath)
      })
    }

    return (
      <ScreenContainer>
        <DSText variant="fluid-heading-700" weight="semiBold" align="center">
          {title}
        </DSText>
        <DSSpacer size={24} direction="vertical" />

        {steps.map((step, i) => (
          <React.Fragment key={i}>
            <StepRow>
              <DSText variant="fluid-paragraph-200" color="secondary">
                {step}
              </DSText>
              <PercentageRow>
                <DSText variant="fluid-paragraph-200" color="secondary">
                  <span ref={(el) => (percentageRefs.current[i] = el)} />
                </DSText>
                <Spinner ref={(el) => (spinnerRefs.current[i] = el)} style={{ display: 'none' }} />
              </PercentageRow>
            </StepRow>
            <DSSpacer size={4} direction="vertical" />
            <BarOuter>
              <BarInner $done={globalAnimationDone} $delay={getStepStartTime(i)} $duration={STEP_DURATIONS[i]} />
            </BarOuter>
            {i < steps.length - 1 && <DSSpacer size={24} direction="vertical" />}
          </React.Fragment>
        ))}

        <DSSpacer size={48} direction="vertical" />
        <DSText variant="fluid-paragraph-200" weight="semiBold" align="center">
          {trustedByText}
        </DSText>
        <DSSpacer size={24} direction="vertical" />

        <TestimonialCarousel testimonials={testimonials} />

        <StickyFooter>
          <StickyFooterContent>
            <DSButton
              variant="primary"
              colorScheme="default"
              size="large"
              width="full"
              isDisabled={!isComplete}
              onPress={handleDiscover}
              testId="discover-plan-button"
            >
              <DSText variant="fluid-paragraph-200" weight="semiBold">
                {buttonText}
              </DSText>
            </DSButton>
          </StickyFooterContent>
        </StickyFooter>
      </ScreenContainer>
    )
  },
)
